import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router";
import {
  METRIC_KEYS, METRIC_LABEL, REPORT_LABELS, formatCount, formatRate, itemValue, linkProblem, parseBrandReport, renderBrandReportPdf, reportDate, reportTotals,
  type BrandReport, type ReportFile, type ReportItem,
} from "@talentkontor/domain";
import { api, ApiError } from "../../data";
import { saveFile } from "../agentur/vertraege/ausfertigung";
import { AgenturKopf, MarkenRahmen, TextMitLinks, useMarkenSprache } from "./MarkeBausteine";
import { invoiceLogo } from "../../model/invoice-document";
import { DateiZeile } from "./Freigabe";
import { tokenOk, type Lang } from "./marke-model";
import { berichtNachweise } from "./bericht-pdf";

/**
 * Bericht für die Marke unter /bericht/:token (0073): veröffentlichte Beiträge mit Link, Kennzahlen (von Hand, dazu
 * Instagram-Werte mit Einwilligung des Creators) und Screenshots als Nachweis, Summen und PDF. Ohne Konto, befristet;
 * jeder Aufruf wird protokolliert (einmal geladen, keine Abfrage, die sich neu lädt). Keine Beträge oder Konditionen.
 */
export function Bericht() {
  const { token = "" } = useParams();
  return <BerichtSeite key={token} token={token} />;
}

function BerichtSeite({ token }: { token: string }) {
  const [report, setReport] = useState<BrandReport | null>(null);
  const [problem, setProblem] = useState<string | null>(tokenOk(token) ? null : "link:invalid");
  const loaded = useRef(false);
  const [lang, setLang] = useMarkenSprache(report?.language ?? null);
  useEffect(() => {
    if (!tokenOk(token) || loaded.current) return;
    loaded.current = true;
    api.rpcAsAnon<unknown>("brand_report_open", { p_token: token })
      .then((r) => setReport(parseBrandReport(r)))
      .catch((e: unknown) => setProblem(e instanceof ApiError && e.hint ? e.hint : "other"));
  }, [token]);
  if (problem || !report) {
    const p = problem ? linkProblem(problem, lang) : null;
    const loading = lang === "en" ? "Loading …" : "Wird geladen …";
    return (
      <MarkenRahmen lang={lang} onLang={setLang} agency={null}>
        <title>{`${p?.title ?? loading} · CreatorOffice`}</title>
        <main id="main" className="auth-shell stack" aria-busy={!problem}>
          {/* Karte wie die übrigen öffentlichen Zustandsseiten (Link abgelaufen, Kit ungültig) */}
          <section className="centerform panel stack">
            <h1>{p?.title ?? loading}</h1>
            {p && <p>{p.text}</p>}
          </section>
        </main>
      </MarkenRahmen>
    );
  }
  return (
    <MarkenRahmen lang={lang} onLang={setLang} agency={report.agency}>
      <title>{`${report.title} · ${report.agency.name}`}</title>
      <main id="main" className="tk-marke stack">
        <AgenturKopf agency={report.agency} />
        <BerichtAnsicht report={report} lang={lang} onFile={(f) => api.linkFile("report", token, f.id)} />
        {api.mode === "demo" && <p className="demo-note">{lang === "en" ? "Demo · All data is fictitious." : "Demo · Alle Daten sind erfunden."}</p>}
      </main>
    </MarkenRahmen>
  );
}

export function berichtDateiname(r: Pick<BrandReport, "title">): string {
  return `${r.title.normalize("NFC").replace(/[^\p{L}\p{N}]+/gu, "-").replace(/^-+|-+$/g, "").slice(0, 80) || "Bericht"}.pdf`;
}

/** Inhalt des Berichts; auch als Vorschau im Agentur-Bereich. Die Sprache der Beschriftung folgt der Seite. */
export function BerichtAnsicht({ report, lang, onFile, embedded = false }: { report: BrandReport; lang: Lang; onFile: (f: ReportFile) => Promise<Blob>; embedded?: boolean }) {
  const L = REPORT_LABELS[lang];
  const t = reportTotals(report.items);
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (!embedded) heading.current?.focus();
  }, [embedded]);
  const kacheln = METRIC_KEYS.filter((k) => t.sums[k] != null);
  const loc = report.agency.locale;
  const [pdf, setPdf] = useState<"busy" | "error" | null>(null);
  const pdfLaden = async () => {
    setPdf("busy");
    try {
      // Nachweise (Screenshots) ins PDF: es behält sie auch, wenn der Link abgelaufen ist
      const proofs = await berichtNachweise(report, onFile);
      // Dokumentkopf: Logo der Agentur (falls hinterlegt)
      const logo = report.agency.logo_path ? (await invoiceLogo(report.agency.logo_path).catch(() => null))?.image ?? null : null;
      saveFile(renderBrandReportPdf({ ...report, language: lang }, { proofs, logo }), berichtDateiname(report));
      setPdf(null);
    } catch {
      setPdf("error");
    }
  };
  return (
    <>
      <div className="stack">
        <div className="eyebrow">{[L.by(report.agency.name), report.brand ? L.forBrand(report.brand) : null].filter(Boolean).join(" · ")}</div>
        {embedded ? <h3>{report.title}</h3> : <h1 ref={heading} tabIndex={-1}>{report.title}</h1>}
        <p className="source-note">{L.generated} {reportDate(report.generated_at, lang, report.agency.time_zone)} · {L.validUntil} {reportDate(report.expires_at, lang, report.agency.time_zone)}</p>
        {report.intro && <TextMitLinks text={report.intro} />}
        <div className="row">
          <button type="button" className="primary" disabled={pdf === "busy"} aria-busy={pdf === "busy"} onClick={() => void pdfLaden()}>
            {pdf === "busy" ? (lang === "en" ? "Preparing PDF …" : "PDF wird erstellt …") : lang === "en" ? "Download PDF" : "Als PDF herunterladen"}
          </button>
        </div>
        {pdf === "error" && <p className="tk-error" role="alert">{lang === "en" ? "The PDF could not be created. Please try again." : "Das PDF ließ sich nicht erstellen. Bitte versuchen Sie es erneut."}</p>}
      </div>
      <section className="panel stack" aria-labelledby="bericht-ueberblick">
        <h2 id="bericht-ueberblick">{L.overview}</h2>
        <div className="tk-marke-kacheln">
          <div className="panel stack"><span className="eyebrow">{L.tileLive}</span><strong className="metric num">{report.items.length}</strong></div>
          {kacheln.map((k) => (
            <div key={k} className="panel stack">
              <span className="eyebrow">{METRIC_LABEL[k][lang]}</span>
              <strong className="metric num">{formatCount(t.sums[k]!, lang, loc)}</strong>
              <small>{t.counts[k]}/{report.items.length}</small>
            </div>
          ))}
          {t.engagementRate != null && (
            <div className="panel stack"><span className="eyebrow">{L.er}</span>
              <strong className="metric num">{formatRate(t.engagementRate, lang, loc)}</strong></div>
          )}
        </div>
        <p className="source-note">{L.sumNote}{t.engagementRate != null ? ` ${L.erNote}` : ""}</p>
        {report.open_count > 0 && <p>{L.open(report.open_count)}</p>}
      </section>
      {report.items.length === 0 && <p className="muted">{L.noItems}</p>}
      {report.items.map((it) => <BeitragKarte key={it.deliverable_id} item={it} lang={lang} locale={loc} onFile={onFile} />)}
    </>
  );
}

function BeitragKarte({ item, lang, locale, onFile }: { item: ReportItem; lang: Lang; locale: string | null; onFile: (f: ReportFile) => Promise<Blob> }) {
  const L = REPORT_LABELS[lang];
  const werte = METRIC_KEYS.map((k) => ({ k, v: itemValue(item, k) })).filter((x) => x.v);
  const ig = item.instagram;
  const extra = ig ? (["likes", "comments", "saved", "shares"] as const).filter((k) => typeof ig[k] === "number") : [];
  const extraLabel = { likes: "Likes", comments: lang === "en" ? "Comments" : "Kommentare", saved: lang === "en" ? "Saves" : "Gespeichert", shares: lang === "en" ? "Shares" : "Geteilt" };
  return (
    <section className="panel stack" aria-label={item.label}>
      <div className="row between">
        <h2>{item.label}</h2>
        {item.live_url && <a className="btn" href={item.live_url} target="_blank" rel="noopener noreferrer nofollow">{lang === "en" ? "Open post ↗" : "Beitrag öffnen ↗"}</a>}
      </div>
      <p className="source-note">{[`${L.creator}: ${item.creator}`, item.published_on ? `${L.date}: ${reportDate(item.published_on, lang)}` : null].filter(Boolean).join(" · ")}</p>
      {werte.length === 0 ? <p className="muted">{L.none}</p> : (
        <dl className="tk-marke-werte">
          {werte.map((x) => (
            <div key={x.k}><dt>{METRIC_LABEL[x.k][lang]}{x.v!.source === "instagram" ? " · Instagram" : ""}</dt><dd>{formatCount(x.v!.value, lang, locale)}</dd></div>
          ))}
          {extra.map((k) => <div key={k}><dt>{extraLabel[k]} · Instagram</dt><dd>{formatCount(ig![k]!, lang, locale)}</dd></div>)}
        </dl>
      )}
      {(item.metrics || ig) && (
        <p className="source-note">{[item.metrics ? L.manual(item.metrics.captured_on ? reportDate(item.metrics.captured_on, lang) : null, item.metrics.source) : null, ig ? L.instagram : null].filter(Boolean).join(" · ")}</p>
      )}
      {item.files.length > 0 && (
        <div className="stack">
          <strong>{L.proof}</strong>
          {item.files.map((f) => <DateiZeile key={f.id} file={f} lang={lang} load={() => onFile(f)} />)}
        </div>
      )}
    </section>
  );
}
